import { L as LogLevel, g as getLogger } from './logger.js';
import { E as ExposedPromise, p as parseWebVTT } from './exposed-promise.js';
import { O as Orientation } from './orientation.js';
import { d as dimensionsToOrientation, f as fetchText } from './fetch.js';

/**
 * Emits log messages to the console, prefixed by the namespaces. The
 * namespaces are formatted to fixed width prefixes, potentially with color.
 *
 * To use the `ConsoleLogHandler`, do:
 *
 * ```ts
 * import { addLogHandler, ConsoleLogHandler } from '@nrk/player-core';
 *
 * addLogHandler(new ConsoleLogHandler());
 * ```
 */
class ConsoleLogHandler {
  constructor(options) {
    var _options$prefixLength, _options$prefixFiller, _options$prefixDelimi, _options$color;

    this.prefixLength = void 0;
    this.prefixFiller = void 0;
    this.prefixDelimiter = void 0;
    this.color = void 0;
    this.goldenRatio = 1.618033988749895;
    this.prefixCache = {};
    this.prefixLength = (_options$prefixLength = options == null ? void 0 : options.prefixLength) != null ? _options$prefixLength : 20;
    this.prefixFiller = (_options$prefixFiller = options == null ? void 0 : options.prefixFiller) != null ? _options$prefixFiller : ' ';
    this.prefixDelimiter = (_options$prefixDelimi = options == null ? void 0 : options.prefixDelimiter) != null ? _options$prefixDelimi : ' |';
    this.color = (_options$color = options == null ? void 0 : options.color) != null ? _options$color : this.isColorSupported();
  }

  log(namespace, level, message, ...messages) {
    if (!(namespace in this.prefixCache)) {
      const prefix = [this.createPrefix(namespace)];

      if (this.color) {
        prefix[0] = `%c${prefix[0]}`;
        prefix.push(`color:hsl(${this.nextHue() * 360},99%,40%);font-weight:bold;`);
      }

      this.prefixCache[namespace] = prefix;
    }

    console[level](...this.prefixCache[namespace], message, ...messages);
  }

  createPrefix(namespace) {
    return `${namespace}${Array(this.prefixLength).join(this.prefixFiller)}`.slice(0, this.prefixLength) + `${this.prefixDelimiter}`;
  }

  nextHue() {
    return Object.keys(this.prefixCache).length * this.goldenRatio;
  }

  isColorSupported() {
    if (typeof window !== 'undefined' && 'chrome' in window) {
      return true;
    }

    if (typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent)) {
      const m = /Firefox\/(\d+\.\d+)/.exec(navigator.userAgent);

      if (m && m[1] && parseFloat(m[1]) >= 31.0) {
        return true;
      }
    }

    return false;
  }

}

const DEFAULT_HISTORY_COUNT = 1000;
const DEFAULT_CONSOLE_PREFIX_LENGTH = 20;

/**
 * Log handler which keeps the last n log entries in memory and makes them
 * available through an async iterator. It can also output the log entries to
 * browser console.
 *
 * Usage:
 *
 *     import { addLogHandler, InternalLogHandler } from 'player-core';
 *
 *     const logHandler = new InternalLogHandler();
 *     addLogHandler(logHandler);
 *
 *     for await (const entry of logHandler.getEntries()) {
 *       // Process entry.
 *     }
 *
 *     logHandler.enableLogToConsole();
 */
class InternalLogHandler {
  constructor(options) {
    var _options$historyCount, _options$consolePrefi;

    this.firstEntry = void 0;
    this.lastEntry = void 0;
    this.numEntries = void 0;
    this.logToConsoleEnabled = false;
    this.consolePrefixLength = void 0;
    this.historyCount = void 0;
    this.historyCount = (_options$historyCount = options == null ? void 0 : options.historyCount) != null ? _options$historyCount : DEFAULT_HISTORY_COUNT;
    this.consolePrefixLength = (_options$consolePrefi = options == null ? void 0 : options.consolePrefixLength) != null ? _options$consolePrefi : DEFAULT_CONSOLE_PREFIX_LENGTH;
    const initEntry = {
      timestamp: Date.now(),
      namespace: 'InternalLogHandler',
      level: LogLevel.Log,
      messages: ['Initialized'],
      next: new ExposedPromise()
    };
    this.firstEntry = initEntry;
    this.lastEntry = initEntry;
    this.numEntries = 1;

    if (options != null && options.logToConsole) {
      this.enableLogToConsole();
    }

    this.keepHistoryCount().catch(console.error);
  }
  /**
   * Log handler input method.
   */


  log(namespace, level, message, ...messages) {
    this.addLogEntry({
      timestamp: Date.now(),
      namespace,
      level,
      messages: [this.serialize(message)].concat(messages.map(m => this.serialize(m))),
      next: new ExposedPromise()
    });
  }
  /**
   * Returns an async iterator which starts at the beginning of internal
   * history and continues to return new log entries after history is
   * exhausted.
   *
   * It can take a log entry as starting point as a way to resume iteration,
   * instead of the beginning of internal history.
   */


  getEntries(from) {
    const iteratorState = {
      current: from != null ? from : this.firstEntry,
      returnFirst: !from
    };
    return {
      async next() {
        if (iteratorState.returnFirst) {
          iteratorState.returnFirst = false;
          return {
            done: false,
            value: iteratorState.current
          };
        }

        try {
          iteratorState.current = await iteratorState.current.next;
        } catch (e) {
          if (e !== 'end') {
            console.log(`Log entry chain crashed: ${e}`);
          }

          return {
            done: true,
            value: undefined
          };
        }

        return {
          done: false,
          value: iteratorState.current
        };
      },

      [Symbol.asyncIterator]() {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this;
      }

    };
  }
  /**
   * Log internal history and all future messages to browser console.
   */


  enableLogToConsole() {
    if (this.logToConsoleEnabled) {
      return;
    }

    this.logToConsoleEnabled = true;
    this.logToConsole().catch(e => {
      this.logToConsoleEnabled = false;
      console.error(e);
    });
  }

  end() {
    this.lastEntry.next.reject('end');
  }

  async logToConsole() {
    const entries = this.getEntries(); // eslint-disable-next-line no-constant-condition

    while (true) {
      const result = await entries.next();

      if (result.done) {
        break;
      }

      const entry = result.value;
      console[entry.level](this.toConsolePrefix(entry.namespace), ...entry.messages, `(${new Date(entry.timestamp).toLocaleTimeString()})`);
    }
  }

  async keepHistoryCount() {
    const entries = this.getEntries(); // eslint-disable-next-line no-constant-condition

    while (true) {
      const result = await entries.next();

      if (result.done) {
        break;
      }

      if (this.numEntries > this.historyCount) {
        await this.removeFirstLogEntry();
      }
    }
  }
  /* Generators aren't transpiled.
   *
  async *getEntries(): AsyncGenerator<LogEntry, void, unknown> {
    let current = this.firstEntry;
    yield current;
     while (true) {
      try {
        current = await current.next;
      } catch (e) {
        console.log('Log entry chain ended:', e);
        break;
      }
      yield current;
    }
  }
   */

  /* Transpiling messes up for await somehow.
   *
  private async logToConsole(): Promise<void> {
    for await (const entry of this.getEntries()) {
      console[entry.level](entry.namespace, ...entry.messages);
    }
  }
  */

  /* Transpiling messes up for await somehow.
   *
  private async keepMaxEntries(): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    for await (const _ of this.getEntries()) {
      if (this.numEntries > this.historyCount) {
        await this.removeFirstLogEntry();
      }
    }
  }
   */


  addLogEntry(newEntry) {
    ++this.numEntries;
    this.lastEntry.next.resolve(newEntry);
    this.lastEntry = newEntry;
  }

  async removeFirstLogEntry() {
    try {
      this.firstEntry = await this.firstEntry.next;
      --this.numEntries;
    } catch (e) {// Ignore.
    }
  }

  serialize(value) {
    // Values that don't result in [object Object] are fine.
    const asString = `${value}`;

    if (asString !== '[object Object]') {
      return asString;
    }

    const seenObjects = [];
    return JSON.stringify(value, (key, value) => {
      // Skip private properties.
      if (typeof key === 'string' && key.charAt(0) === '_') {
        return undefined;
      } // Avoid circular pointers by only serializing objects once.


      if (typeof value === 'object' && value !== null) {
        if (seenObjects.includes(value)) {
          return '...';
        }

        seenObjects.push(value);
      } // eslint-disable-next-line @typescript-eslint/no-unsafe-return


      return value;
    }, 2);
  }

  toConsolePrefix(namespace) {
    return `${namespace}${Array(this.consolePrefixLength).join(' ')}`.slice(0, this.consolePrefixLength) + '|';
  }

}

/**
 * Observe screen orientation changes.
 *
 * Intended purpose: Monitor screen orientation on phones/tablets and rotate
 * the video player when it's in fullscreen and the video orientation differs
 * from the screen orientation.
 *
 * It's roughly modelled after the W3C observers (Performance-, Intersection-,
 * Resize-, etc).
 */
class ScreenOrientationObserver {
  constructor(callback) {
    this.callback = void 0;
    this.observing = false;
    this.currentEntry = void 0;
    this.boundHandleEvent = void 0;
    this.callback = callback;
    this.boundHandleEvent = this.handleEvent.bind(this);
  }

  observe(options) {
    if (!this.observing) {
      var _window$screen$orient;

      (_window$screen$orient = window.screen.orientation) == null ? void 0 : _window$screen$orient.addEventListener('change', this.boundHandleEvent);
      document.addEventListener('orientationchange', this.boundHandleEvent);
      this.observing = true;
    }

    if (options != null && options.immediateUpdate) {
      if (this.currentEntry) {
        this.callback([this.currentEntry], this);
      } else {
        // Fake an event to potentially trigger the callback.
        this.handleEvent();
      }
    }
  }

  disconnect() {
    if (this.observing) {
      var _window$screen$orient2;

      (_window$screen$orient2 = window.screen.orientation) == null ? void 0 : _window$screen$orient2.removeEventListener('change', this.boundHandleEvent);
      document.removeEventListener('orientationchange', this.boundHandleEvent);
      this.observing = false;
    }
  }

  handleEvent() {
    var _this$currentEntry, _this$currentEntry2;

    let entry = this.getEntryByStandard();

    if (!entry) {
      entry = this.getEntryByLegacy();
    }

    if (entry && (((_this$currentEntry = this.currentEntry) == null ? void 0 : _this$currentEntry.orientation) !== entry.orientation || ((_this$currentEntry2 = this.currentEntry) == null ? void 0 : _this$currentEntry2.primary) !== entry.primary)) {
      this.currentEntry = entry;
      this.callback([entry], this);
    }
  }
  /**
   * Supported by modern browsers.
   *
   * Ignoring mozOrientation and msOrientation legacy properties.
   */


  getEntryByStandard() {
    var _window$screen$orient3;

    const type = (_window$screen$orient3 = window.screen.orientation) == null ? void 0 : _window$screen$orient3.type;

    if (type) {
      return {
        orientation: /^portrait/.exec(type) ? Orientation.Portrait : Orientation.Landscape,
        primary: !/secondary$/.exec(type)
      };
    }

    return undefined;
  }
  /**
   * Support by iOS.
   */


  getEntryByLegacy() {
    // screen.width and screen.height are not updated on iOS when rotating.
    const orientation = dimensionsToOrientation(window.innerWidth, window.innerHeight);
    const angle = window.orientation;

    if (orientation && typeof angle === 'number') {
      return {
        orientation,
        primary: angle === 0 || angle === 90
      };
    }
  }

}

/**
 * Load and parse a WebVTT file. The cues can be transformed when loaded, and
 * the result is stored in each cue's `data` property.
 *
 * Example loading a WebVTT file with image URLs:
 *
 *     const images = await WebVTTDataFile.loadUrlCues('https://...');
 *     const image = document.createElement('img');
 *     image.src = images.search(10).data.url; // The image at 10s.
 */
class WebVTTDataFile {
  constructor(cues) {
    this.cues = void 0;
    this.cues = cues;
  }
  /**
   * Load a WebVTT file and potentially transform each cue into some useful
   * data available on the cues' `data` property.
   */


  static async load(url, transform) {
    const data = await fetchText(url);
    const webvtt = parseWebVTT(data); // Add data properties to all the cues.

    const cues = webvtt.cues.map(cue => {
      const c = cue;
      c.data = transform == null ? void 0 : transform(cue);
      return c;
    });
    return new WebVTTDataFile(cues);
  }
  /**
   * Load a WebVTT file containing URLs and set data.url on each cue object to
   * the the absolute URL (using the WebVTT URL as base).
   */


  static async loadUrlCues(url) {
    return WebVTTDataFile.load(url, cue => ({
      url: new URL(cue.text, url).toString()
    }));
  }
  /**
   * Search for a single cue on the timestamp given
   */


  search(time) {
    const index = this.searchIndex(time);
    return this.getCue(index);
  }
  /**
   * Search for a list of cues, before and after the timestamp searched for,
   * with the specified interval between cues
   *
   * @param time - timestamp to search for
   * @param interval - interval (seconds) between cues returned
   * @param context - number of cues before and after the cue found on timestamp (if any)
   * @returns A list of found cues or a list of undefined, the length of context * 2 + 1
   */


  searchCues(time, interval, context) {
    const mainCueIndex = this.searchIndex(time);
    const resultLength = context * 2 + 1;

    if (!mainCueIndex) {
      return new Array().fill(undefined, 0, resultLength);
    }

    const mainCueStartTime = this.cues[mainCueIndex].startTime;
    let cues = new Array();
    let increment = 1;
    cues.push(this.getCue(mainCueIndex));

    while (cues.length < resultLength) {
      const before = this.searchIndex(mainCueStartTime - interval * increment);
      const after = this.searchIndex(mainCueStartTime + interval * increment);
      cues = [this.getCue(before), ...cues, this.getCue(after)];
      increment++;
    }

    return cues;
  }

  getCue(index) {
    if (!index) {
      return;
    }

    return this.cues[index] ? this.cues[index] : undefined;
  }
  /**
   * Binary search for the index of the cue matching a given time. The cues are assumed
   * to not overlap.
   */


  searchIndex(time) {
    let low = 0;
    let high = this.cues.length;

    while (low < high) {
      // Equivalent to Math.floor((low + high) / 2):
      const mid = low + high >>> 1;

      if (this.cues[mid].startTime > time) {
        high = mid;
      } else {
        low = mid + 1;
      }
    }

    const cue = this.cues[low - 1];
    return cue && cue.endTime > time ? low - 1 : undefined;
  }

}

const logger = getLogger('UnlockMediaElement');
const unlockedMap = new WeakMap();
/**
 * Apply a user interaction to a media element so that, if the media source and
 * call to play comes later, possibly disconnected from the user interaction,
 * the playback will not be blocked by autoplay policy. Use by connecting an
 * event handler (e.g. click on poster) directly to the call of this method.
 * There's a WeakMap reference guarding the element from multiple calls to
 * unlock.
 */

function unlockMediaElement(element) {
  if (unlockedMap.has(element)) {
    logger.log('Element already unlocked.');
    return;
  }

  if (element.currentSrc) {
    // With a source, just call play.
    logger.log('Element has source. No need to unlock.');
    return;
  } // Calling load with track elements and no source will trigger an error on
  // iOS devices.


  if (element.querySelector('track')) {
    logger.log('Element has tracks but no source. Not touching.');
    return;
  }

  unlockedMap.set(element, true);
  logger.log('Calling load on media element to unlock it.');
  element.load();
}

export { ConsoleLogHandler as C, InternalLogHandler as I, ScreenOrientationObserver as S, WebVTTDataFile as W, unlockMediaElement as u };
